/**
 * WARNING:
 * This component MAY receive an optional caseId.
 * Page-level components MUST NOT copy this pattern.
 * All pages MUST call requireCaseId().
 */
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Search, User, ChevronDown, Settings, LogOut, HelpCircle, Home, CreditCard, Bell, Menu, Shield } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useAuthState, useAuthActions } from '@/auth/AuthProvider';
import { useCases } from '@/hooks/useCases';
import BrandWordmark from '@/components/brand/BrandWordmark';
import { updateCurrentCase } from '@/lib/updateCurrentCase';
import { Timestamp } from 'firebase/firestore';
import { CreateDocumentButton } from '@/components/documents/CreateDocumentButton';
import NotificationBell from '@/components/notifications/NotificationBell';
import { useUnifiedNotificationCount } from '@/hooks/useUnifiedNotificationCount';

export default function TopHeader({ onMobileMenuToggle }: { onMobileMenuToggle?: () => void }) {
  const { user } = useAuthState();
  const { signOutUser } = useAuthActions();
  const { data: allCases } = useCases();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isCaseMenuOpen, setIsCaseMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const caseMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { caseId: currentCaseId } = useParams<{ caseId?: string }>();
  const { totalUnread } = useUnifiedNotificationCount({ activeCaseId: currentCaseId });

  const handleSignOut = async () => {
    try {
      await signOutUser();
      navigate('/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  // Check if user is admin
  useEffect(() => {
    let isMounted = true;
    
    if (!user) {
      if (isMounted) setIsAdmin(false);
      return;
    }
    
    user.getIdTokenResult().then((tokenResult) => {
      if (isMounted) {
        setIsAdmin(tokenResult.claims.admin === true);
      }
    }).catch(() => {
      if (isMounted) {
        setIsAdmin(false);
      }
    });
    
    return () => { isMounted = false; };
  }, [user]);

  // Get current case info
  const currentCase = allCases?.find(c => c.id === currentCaseId);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (caseMenuRef.current && !caseMenuRef.current.contains(event.target as Node)) {
        setIsCaseMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-700 bg-slate-900/95 backdrop-blur-sm px-4 sm:px-6 shadow-sm">
      {/* Left: Hamburger (mobile), Logo & Case Dropdown */}
      <div className="flex items-center gap-2 sm:gap-4">
        {/* Hamburger button — visible on mobile only */}
        <button
          type="button"
          onClick={onMobileMenuToggle}
          className="lg:hidden rounded-lg p-2 text-slate-300 hover:bg-slate-800 hover:text-white transition focus:outline-none focus:ring-2 focus:ring-orange-600"
          aria-label="Open navigation menu"
        >
          <Menu size={20} />
        </button>

        <Link
          to="/dashboard"
          className="flex items-center gap-3 transition hover:opacity-80"
          aria-label="Go to Dashboard"
        >
          <BrandWordmark darkText={false} className="h-8" />
        </Link>

        {/* Case Dropdown - Always show when cases exist */}
        {allCases && allCases.length > 0 && (
          <div className="relative" ref={caseMenuRef}>
            <button
              type="button"
              onClick={() => setIsCaseMenuOpen(!isCaseMenuOpen)}
              className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-2 sm:px-3 py-2 text-sm font-medium text-slate-100 transition hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-600"
              aria-expanded={isCaseMenuOpen ? "true" : "false"}
            >
              <span className="max-w-[120px] sm:max-w-[200px] truncate">
                {currentCase ? currentCase.title : 'Select a case'}
              </span>
              <ChevronDown size={16} className={`transition-transform ${isCaseMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {isCaseMenuOpen && (
              <div className="absolute left-0 top-full mt-2 w-80 rounded-lg border border-slate-700 bg-slate-900 shadow-xl max-h-96 overflow-y-auto">
                <div className="p-2">
                  {allCases.map((caseItem) => {
                    // Phase 4: Get jurisdiction label and last updated date
                    const jurisdictionKey = caseItem.jurisdictionKey || caseItem.jurisdiction;
                    const jurisdictionLabel = jurisdictionKey && typeof jurisdictionKey === 'string'
                      ? jurisdictionKey.replace('US-', '').replace(/-/g, ' ')
                      : null;

                    // Safely handle Timestamp - could be Timestamp, Date, or undefined
                    const lastUpdated = caseItem.updatedAt instanceof Timestamp 
                      ? caseItem.updatedAt.toDate() 
                      : null;
                    const lastUpdatedText = lastUpdated
                      ? (() => {
                          const now = new Date();
                          const diffMs = now.getTime() - lastUpdated.getTime();
                          const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

                          if (diffDays === 0) return 'Today';
                          if (diffDays === 1) return 'Yesterday';
                          if (diffDays < 7) return `${diffDays}d ago`;
                          if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
                          if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
                          return lastUpdated.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                        })()
                      : null;

                    return (
                      <Link
                        key={caseItem.id}
                        to={`/cases/${caseItem.id}`}
                        onClick={() => {
                          // Phase 1: Track case switch
                          if (user?.uid && caseItem.id !== currentCaseId) {
                            updateCurrentCase(user.uid, caseItem.id);
                          }
                          setIsCaseMenuOpen(false);
                        }}
                        className={`block rounded-md px-3 py-2.5 transition ${
                          caseItem.id === currentCaseId
                            ? 'bg-slate-800 border-l-2 border-orange-600'
                            : 'text-slate-300 hover:bg-slate-800'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate text-slate-100">
                              {caseItem.title}
                            </div>
                          </div>
                          {lastUpdatedText && (
                            <div className="text-xs flex-shrink-0 text-slate-400">
                              {lastUpdatedText}
                            </div>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
                <div className="border-t border-slate-700 p-2">
                  <Link
                    to="/cases/new"
                    onClick={() => setIsCaseMenuOpen(false)}
                    className="block rounded-md bg-orange-600 px-3 py-2 text-center text-sm font-medium text-white transition hover:bg-orange-700"
                  >
                    + New Case
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right: Icons, Help, Date, User */}
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Home Button — desktop only (accessible via sidebar on mobile) */}
        <Link
          to="/dashboard"
          className="hidden lg:flex rounded-lg border border-slate-700 bg-slate-800 p-2 text-slate-300 transition hover:bg-slate-700 hover:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-600"
          aria-label="Dashboard"
          title="Dashboard"
        >
          <Home size={18} />
        </Link>

        {/* Search — desktop only */}
        <Link
          to="/search"
          className="hidden lg:flex rounded-lg border border-slate-700 bg-slate-800 p-2 text-slate-300 transition hover:bg-slate-700 hover:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-600"
          aria-label="Search"
          title="Global Search"
        >
          <Search size={18} />
        </Link>

        {/* Help — desktop only */}
        <Link
          to="/help"
          className="hidden lg:flex rounded-lg border border-slate-700 bg-slate-800 p-2 text-slate-300 transition hover:bg-slate-700 hover:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-600"
          aria-label="Help Center"
          title="Help Center"
        >
          <HelpCircle size={18} />
        </Link>

        {/* Notification Bell — WO7-P2-3 */}
        <NotificationBell totalUnread={totalUnread} />

        {/* Create Document Button - Phase 4: Omnipresent in case context, hidden on mobile */}
        {currentCaseId && (
          <span className="hidden sm:inline-flex">
            <CreateDocumentButton caseId={currentCaseId} variant="primary" size="sm" />
          </span>
        )}

        {/* Admin Dashboard Button */}
        {isAdmin && (
          <Link
            to="/admin"
            className="flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm font-medium text-orange-900 transition hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-orange-600"
            title="Admin Dashboard"
          >
            <Shield size={16} />
            <span className="hidden sm:inline">Admin</span>
          </Link>
        )}

        {/* User Menu */}
        <div className="relative" ref={userMenuRef} data-testid="user-menu">
          <button
            type="button"
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="rounded-lg border border-orange-600 bg-orange-600 p-2 text-white transition hover:bg-orange-700 hover:border-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-600"
            aria-expanded={isUserMenuOpen ? "true" : "false"}
            aria-label="Account Menu"
            data-testid="user-menu-trigger"
          >
            <User size={18} />
          </button>

          {isUserMenuOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 rounded-lg border border-slate-700 bg-slate-900 shadow-xl">
              <div className="border-b border-slate-700 px-4 py-3">
                <p className="text-xs font-medium text-slate-400">Signed in as</p>
                <p className="mt-0.5 truncate text-sm font-medium text-slate-100">{user?.email || 'User'}</p>
              </div>
              <div className="p-2">
                <Link
                  to="/profile"
                  onClick={() => setIsUserMenuOpen(false)}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-gray-300 transition hover:bg-slate-700"
                >
                  <User size={16} />
                  <span>Profile & Billing</span>
                </Link>
                <Link
                  to="/settings"
                  onClick={() => setIsUserMenuOpen(false)}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-gray-300 transition hover:bg-slate-700"
                >
                  <Settings size={16} />
                  <span>Settings</span>
                </Link>
                <Link
                  to="/subscribe"
                  onClick={() => setIsUserMenuOpen(false)}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-orange-400 transition hover:bg-orange-950/30 font-medium"
                >
                  <CreditCard size={16} />
                  <span>Upgrade Plan</span>
                </Link>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-red-400 transition hover:bg-red-950/50"
                >
                  <LogOut size={16} />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
